import React, { useState } from "react";
import { Dialog, Grow, IconButton, TextField, Button } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import DirectionsRunRoundedIcon from "@mui/icons-material/DirectionsRunRounded";
import ViewKanbanRoundedIcon from "@mui/icons-material/ViewKanbanRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { Formik, Form, ErrorMessage } from "formik";
import { sprintSchema, kanbanSchema } from "../../Validations/schema";
import { DurationList } from "../../utilities/Json";
import SelectMenuTextField from "../SelectMenuTextField";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import Textarea from "@mui/joy/Textarea";
import axios from "axios";
import API_BASE_URL from "../config/appConfig";
import { useSelector, useDispatch } from "react-redux";
import { getLastFiveActiveSprintInfo, getSprintsList } from "../../common/Redux/Reducer";
import { showSnackbar } from "../../common/Redux/Reducer";
import "../Modal/modal.scss";
import "./sprintStartModal.scss";
import { useToast } from "../Toast";

const SmoothGrow = React.forwardRef(function SmoothGrow(props, ref) {
  return <Grow ref={ref} timeout={{ enter: 320, exit: 200 }} {...props} />;
});

/* what each format is for, shown before the team commits to one */
export const BOARD_FORMATS = [
  {
    type: "scrum",
    title: "Sprint",
    icon: DirectionsRunRoundedIcon,
    tagline: "Time-boxed iteration",
    points: [
      "Commit to a set of issues for 1–4 weeks",
      "Best when the scope for the period is known",
      "Tracks progress against the sprint end date",
    ],
  },
  {
    type: "kanban",
    title: "Kanban",
    icon: ViewKanbanRoundedIcon,
    tagline: "Continuous flow to a release",
    points: [
      "Pull work in as capacity frees up — no fixed scope",
      "Limit work in progress on each column",
      "Forecasts whether the release date will be met",
    ],
  },
];

/* common release windows, counted from the start date */
export const RELEASE_PRESETS = [
  { label: "2 weeks", days: 14 },
  { label: "1 month", days: 30 },
  { label: "6 weeks", days: 42 },
  { label: "3 months", days: 90 },
];

const DAY_MS = 24 * 60 * 60 * 1000;

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

/** Mon–Fri days from start (inclusive) to end (exclusive). */
export const workingDaysBetween = (start, end) => {
  if (!start || !end || end <= start) return 0;
  let count = 0;
  for (let d = new Date(start); d < end; d = addDays(d, 1)) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count += 1;
  }
  return count;
};

/**
 * What the chosen dates mean for the team, in the units the board reports:
 * the timeline, and the pace needed to finish what is planned in it.
 */
export const planSummary = (issueCount, start, end) => {
  if (!start || !end || end <= start) return null;
  const days = Math.max(1, Math.round((end - start) / DAY_MS));
  const weeks = days / 7;
  const perWeek = issueCount / weeks;
  let pace;
  if (!issueCount) pace = "Add issues to see the pace you'll need.";
  else if (perWeek >= 1) pace = `About ${Math.round(perWeek * 10) / 10} issues a week to finish all ${issueCount}.`;
  else pace = `About 1 issue every ${Math.max(1, Math.round(1 / perWeek))} week${Math.round(1 / perWeek) === 1 ? "" : "s"} to finish ${issueCount === 1 ? "it" : `all ${issueCount}`}.`;
  return { days, workingDays: workingDaysBetween(start, end), pace };
};

const DATE_FORMAT = "dd MMM yyyy, hh:mm a";

/* picker props shared by every date field: the label sits above the field,
   so none inside it, and the same height as the text inputs */
const pickerProps = (ariaLabel) => ({
  format: DATE_FORMAT,
  slotProps: { textField: { size: "small", fullWidth: true, inputProps: { "aria-label": ariaLabel } } },
});

const SprintStartModal = ({
  setOpenModal,
  sprintData,
  isSprintEditable,
  issuesCount,
  setIsSprintEditable = () => { }
}) => {
  const [boardType, setBoardType] = useState(sprintData?.boardType || "scrum");
  // a new board picks its format first; editing keeps the one it has
  const [step, setStep] = useState(isSprintEditable ? "details" : "format");
  const isKanban = boardType === "kanban";

  const [startDate, setStartDate] = useState(sprintData?.startDate ? new Date(sprintData?.startDate) : new Date());
  const [duration, setDuration] = useState(sprintData?.duration && sprintData?.duration !== "Release" ? sprintData?.duration : "2 week");
  const estimatedEndDate = new Date(startDate);
  estimatedEndDate.setDate(estimatedEndDate.getDate() + parseInt(2) * 7);
  const [endDate, setEndDate] = useState(sprintData?.endDate ? new Date(sprintData?.endDate) : estimatedEndDate);

  const defaultRelease = new Date(startDate);
  defaultRelease.setDate(defaultRelease.getDate() + 30);
  const [releaseDate, setReleaseDate] = useState(
    sprintData?.releaseDate ? new Date(sprintData.releaseDate) : defaultRelease
  );

  const dispatch = useDispatch();
  const toast = useToast();
  const { Projects } = useSelector((state) => state.ProjamAppSlice)
  let backlogId = Projects?.selectedProject?.selectedProjectBackLogId
  let projectId = Projects?.selectedProject?.selectedProjectData?._id

  const count = Number.isFinite(issuesCount) ? issuesCount : (sprintData?.issues?.length || 0);

  /* the columns work flows through — not the first (queue) and not Done —
     which is where a Kanban WIP limit matters */
  const flowColumns = (Projects?.selectedProject?.selectedProjectData?.IssueStatus || [])
    .filter((status, index) => status?._id && index > 0 && String(status.title).trim().toLowerCase() !== "done");
  const [wipLimit, setWipLimit] = useState("");
  const wipValid = wipLimit === "" || (/^\d+$/.test(wipLimit) && Number(wipLimit) >= 1 && Number(wipLimit) <= 99);
  const noun = isKanban ? "board" : "sprint";

  const calculateEndDate = (startDate, duration) => {
    const newEndDate = new Date(startDate);
    const durationWeeks = parseInt(duration.split(" ")[0]);
    newEndDate.setDate(newEndDate.getDate() + durationWeeks * 7 - 1)
    return newEndDate;
  };

  const initialValues = {
    name: sprintData?.name || "",
    duration: duration,
    startDate: startDate,
    endDate: endDate,
    releaseDate: releaseDate,
    sprintGoal: sprintData?.sprintGoal || "",
  };

  const handleDurationChange = (event, setFieldValue) => {
    const selectedDuration = event.target.value;
    setDuration(selectedDuration);
    setFieldValue("duration", selectedDuration);

    if (selectedDuration !== "Custom") {
      const newEndDate = calculateEndDate(startDate, selectedDuration);
      setEndDate(newEndDate);
      setFieldValue("endDate", newEndDate);
    } else {
      setEndDate(null);
      setFieldValue("endDate", null);
    }
  };

  const handleStartDateChange = (newValue, setFieldValue) => {
    setStartDate(newValue);
    setFieldValue("startDate", newValue);
    if (!isKanban && duration !== "Custom") {
      const numWeeks = parseInt(duration);
      const newEndDate = new Date(newValue);
      newEndDate.setDate(newEndDate.getDate() + numWeeks * 7);
      setEndDate(newEndDate);
      setFieldValue("endDate", newEndDate);
    }
  };

  const handleStartSprint = async (values, { setSubmitting }) => {
    const common = {
      name: values.name,
      startDate: values.startDate,
      sprintGoal: values.sprintGoal,
      boardType,
      completed: false,
      ...(isSprintEditable ? {} : { active: true }),
    };
    if (isKanban && !wipValid) {
      setSubmitting(false);
      return;
    }
    const startingWip = isKanban && !isSprintEditable && wipLimit
      ? { wipLimits: Object.fromEntries(flowColumns.map((column) => [column._id, Number(wipLimit)])) }
      : {};
    const data = isKanban
      ? { ...common, releaseDate: releaseDate || values.releaseDate, ...startingWip }
      : { ...common, duration: values.duration, endDate: endDate || values.endDate };

    if (isSprintEditable) {
      const hasChanges = Object.keys(initialValues).some(
        key => String(initialValues[key]) !== String(values[key])
      ) || boardType !== (sprintData?.boardType || "scrum");
      if (!hasChanges) {
        dispatch(showSnackbar("No changes, Please update"));
        setSubmitting(false);
        return;
      }
    }

    try {
      await axios.put(
        `${API_BASE_URL}/sprints/updateSprint/${sprintData?._id}`,
        data
      );
      dispatch(getSprintsList({ backlogId: backlogId, filterAssigneesArray: [] }))
      dispatch(getLastFiveActiveSprintInfo({ backlogId, projectId }))
      setOpenModal(false);
      toast.success(
        isSprintEditable
          ? `${isKanban ? "Kanban board" : "Sprint"} updated`
          : `${isKanban ? "Kanban board" : "Sprint"} started`
      );
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
        (isSprintEditable ? `Could not update the ${noun}` : `Could not start the ${noun}`)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpenModal(false);
    setIsSprintEditable(false);
  };

  const format = BOARD_FORMATS.find((f) => f.type === boardType);
  const FormatIcon = format.icon;
  const title = isSprintEditable
    ? `Update ${isKanban ? "Kanban board" : "sprint"}`
    : step === "format"
      ? "How will this team work?"
      : `Start ${isKanban ? "Kanban board" : "sprint"}`;

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      aria-labelledby="sprint-start-dialog-title"
      fullWidth
      maxWidth="sm"
      TransitionComponent={SmoothGrow}
      PaperProps={{ className: "pj-modal pj-modal--form pj-modal--sprint-start" }}
      slotProps={{ backdrop: { className: "pj-modal-backdrop" } }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={isKanban ? kanbanSchema : sprintSchema}
        onSubmit={handleStartSprint}
        enableReinitialize={false}
      >
        {({ handleChange, handleBlur, setFieldValue, values, isSubmitting }) => (
          <Form>
            <div className="pj-modal-body">
              <IconButton className="pj-modal-close" size="small" onClick={handleClose} aria-label="Close">
                <CloseRoundedIcon fontSize="small" />
              </IconButton>

              <div className="pj-modal-head">
                <span className="pj-modal-badge">
                  {step === "format" ? <RocketLaunchRoundedIcon /> : <FormatIcon />}
                </span>
                <div>
                  <h3 className="pj-modal-title" id="sprint-start-dialog-title">{title}</h3>
                  <p className="pj-modal-sub">
                    {step === "format"
                      ? `${count} ${count === 1 ? "issue" : "issues"} will be included. Pick the format that fits — you can't mix both on one board.`
                      : <>
                          {!isSprintEditable ? <span className="sb-step">Step 2 of 2 · {format.title}</span> : null}
                          {count} {count === 1 ? "issue" : "issues"} included. Fields marked <span className="pj-req">*</span> are required.
                        </>}
                  </p>
                </div>
              </div>

              {step === "format" ? (
                <div className="sb-formats" role="radiogroup" aria-label="Board format">
                  {BOARD_FORMATS.map(({ type, title: label, icon: Icon, tagline, points }) => (
                    <button
                      key={type}
                      type="button"
                      role="radio"
                      aria-checked={boardType === type}
                      className={`sb-format ${boardType === type ? "is-selected" : ""}`}
                      onClick={() => setBoardType(type)}
                    >
                      <span className="sb-format__head">
                        <span className="sb-format__icon"><Icon /></span>
                        <span>
                          <span className="sb-format__title">{label}</span>
                          <span className="sb-format__tagline">{tagline}</span>
                        </span>
                        <CheckCircleRoundedIcon className="sb-format__check" />
                      </span>
                      <ul className="sb-format__points">
                        {points.map((point) => <li key={point}>{point}</li>)}
                      </ul>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="pj-modal-scroll">
                  {isSprintEditable ? (
                    <span className={`sb-type-chip ${isKanban ? "is-kanban" : ""}`}>
                      <FormatIcon /> {format.title}
                    </span>
                  ) : null}

                  <div className="pj-field-row">
                    <div className="pj-field-group">
                      <label className="pj-field-label" htmlFor="sprint-name">
                        {isKanban ? "Board name" : "Sprint name"}<span className="pj-req">*</span>
                      </label>
                      <TextField
                        id="sprint-name"
                        name="name"
                        onChange={handleChange("name")}
                        onBlur={handleBlur("name")}
                        value={values.name}
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                      <ErrorMessage name="name" component="span" className="error-message text-danger" />
                    </div>

                    {isKanban ? (
                      <div className="pj-field-group">
                        <label className="pj-field-label">Start date<span className="pj-req">*</span></label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DateTimePicker
                            {...pickerProps("Start date")}
                            value={startDate}
                            onChange={(newValue) => handleStartDateChange(newValue, setFieldValue)}
                            disabled={isSprintEditable}
                          />
                        </LocalizationProvider>
                      </div>
                    ) : (
                      <div className="pj-field-group">
                        <label className="pj-field-label">Duration<span className="pj-req">*</span></label>
                        <SelectMenuTextField
                          labelText="Duration"
                          name="duration"
                          options={DurationList}
                          onChange={(e) => handleDurationChange(e, setFieldValue)}
                          value={values.duration}
                          onBlur={handleBlur("duration")}
                          defaultValue={duration}
                        />
                        {!duration && (
                          <ErrorMessage name="duration" component="span" className="error-message text-danger" />
                        )}
                      </div>
                    )}
                  </div>

                  {isKanban ? (
                    <div className="pj-field-group">
                      <label className="pj-field-label">Release date<span className="pj-req">*</span></label>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                          {...pickerProps("Release date")}
                          minDateTime={startDate}
                          value={releaseDate}
                          onChange={(newValue) => {
                            setReleaseDate(newValue);
                            setFieldValue("releaseDate", newValue);
                          }}
                        />
                      </LocalizationProvider>
                      <div className="sb-presets" role="group" aria-label="Quick release dates">
                        {RELEASE_PRESETS.map(({ label, days }) => {
                          const date = addDays(startDate, days);
                          const selected = releaseDate && Math.abs(releaseDate - date) < 60 * 1000;
                          return (
                            <button
                              key={label}
                              type="button"
                              className={`sb-preset ${selected ? "is-selected" : ""}`}
                              aria-pressed={Boolean(selected)}
                              onClick={() => {
                                setReleaseDate(date);
                                setFieldValue("releaseDate", date);
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      <ErrorMessage name="releaseDate" component="span" className="error-message text-danger" />
                    </div>
                  ) : (
                    <div className="pj-field-row">
                      <div className="pj-field-group">
                        <label className="pj-field-label">Start date<span className="pj-req">*</span></label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DateTimePicker
                            {...pickerProps("Start date")}
                            minDate={startDate}
                            value={startDate}
                            onChange={(newValue) => handleStartDateChange(newValue, setFieldValue)}
                            disabled={isSprintEditable}
                          />
                        </LocalizationProvider>
                        <ErrorMessage name="startDate" component="span" className="error-message text-danger" />
                      </div>

                      <div className="pj-field-group">
                        <label className="pj-field-label">End date<span className="pj-req">*</span></label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DateTimePicker
                            {...pickerProps("End date")}
                            minDate={new Date()}
                            value={endDate}
                            onChange={(newValue) => {
                              setEndDate(newValue);
                              setFieldValue("endDate", newValue);
                            }}
                          />
                        </LocalizationProvider>
                        {!endDate && (
                          <ErrorMessage name="endDate" component="span" className="error-message text-danger" />
                        )}
                      </div>
                    </div>
                  )}

                  <div className="pj-field-group">
                    <label className="pj-field-label" htmlFor="sprint-goal">
                      {isKanban ? "Release goal" : "Sprint goal"}<span className="pj-req">*</span>
                    </label>
                    <Textarea
                      // Joy puts a bare id on its wrapper; the label needs the textarea itself
                      slotProps={{ textarea: { id: "sprint-goal" } }}
                      name="sprintGoal"
                      onChange={handleChange("sprintGoal")}
                      onBlur={handleBlur("sprintGoal")}
                      value={values.sprintGoal}
                      color="neutral"
                      minRows={3}
                      className="text-area-field"
                      size="sm"
                      placeholder={isKanban ? "What ships on the release date?" : "What should this sprint achieve?"}
                    />
                    <ErrorMessage name="sprintGoal" component="span" className="error-message text-danger" />
                  </div>

{isKanban && !isSprintEditable && flowColumns.length ? (
                    <div className="pj-field-group">
                      <label className="pj-field-label" htmlFor="sprint-wip">
                        WIP limit <span className="sb-optional">optional</span>
                      </label>
                      <div className="sb-wip">
                        <TextField
                          id="sprint-wip"
                          size="small"
                          value={wipLimit}
                          onChange={(e) => setWipLimit(e.target.value.trim())}
                          placeholder="e.g. 3"
                          error={!wipValid}
                          inputProps={{ inputMode: "numeric", "aria-describedby": "sprint-wip-help" }}
                        />
                        <span id="sprint-wip-help" className="sb-wip__help">
                          {wipValid
                            ? <>Most issues allowed at once in <strong>{flowColumns.map((c) => c.title).join(", ")}</strong>. Fine-tune each column on the board.</>
                            : "A whole number from 1 to 99, or leave it empty."}
                        </span>
                      </div>
                    </div>
                  ) : null}

                  {(() => {
                    const plan = planSummary(count, startDate, isKanban ? releaseDate : endDate);
                    if (!plan) return null;
                    return (
                      <div className="sb-plan" aria-label={isKanban ? "Release plan" : "Sprint plan"}>
                        <div className="sb-plan__row">
                          <EventAvailableRoundedIcon />
                          <span>
                            <strong>{plan.days} days</strong> to {isKanban ? "release" : "the sprint end"}
                            <em> · {plan.workingDays} working days</em>
                          </span>
                        </div>
                        <div className="sb-plan__row">
                          <SpeedRoundedIcon />
                          <span>{plan.pace}</span>
                        </div>
                        <div className="sb-plan__row">
                          <InsightsRoundedIcon />
                          <span>
                            The board tracks done vs pending and warns you if the pace falls behind
                            {isKanban ? " the release date." : " the sprint end."}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="pj-modal-actions">
              {step === "format" ? (
                <>
                  <Button className="pj-btn pj-btn--quiet" type="button" onClick={handleClose}>Cancel</Button>
                  <Button className="pj-btn pj-btn--primary" type="button" onClick={() => setStep("details")}>
                    Continue with {format.title}
                  </Button>
                </>
              ) : (
                <>
                  {isSprintEditable ? (
                    <Button className="pj-btn pj-btn--quiet" type="button" onClick={handleClose}>Close</Button>
                  ) : (
                    <Button className="pj-btn pj-btn--quiet" type="button" onClick={() => setStep("format")} startIcon={<ArrowBackRoundedIcon />}>
                      Back
                    </Button>
                  )}
                  <Button className="pj-btn pj-btn--primary" type="submit" disabled={isSubmitting || (isKanban && !wipValid)}>
                    {isSprintEditable
                      ? `Update ${isKanban ? "board" : "sprint"}`
                      : `Start ${isKanban ? "Kanban board" : "sprint"}`}
                  </Button>
                </>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default SprintStartModal;
